import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { FaChevronLeft, FaEye } from "react-icons/fa";
import { getOrdersByUserId } from "../actions/ordersActions";
import PaymentModal from "../components/PaymentModal";
import ReceiptPreviewModal from "../components/ReceiptPreviewModal";
import Receipt from "../components/Receipt";
import "../styles/orderdetails.css";
import "../styles/paymentmodal.css";
import "../styles/receipt.css";
import "../styles/receiptpreviewmodal.css";
import AccountLayout from "../components/AccountLayout";
import { OrderDetailPageSkeleton } from "../components/HomeSkeletons";
import formatMoney from "../components/formatMoney";
import OrderSummary from "../components/Ordersummary";
import ShippingProgress from "../components/ShippingProgress";
import { useImage } from "../contexts/ImageContext";

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { orders = [], loading, error } = useSelector((state) => state.orders);
  const { getImageUrl } = useImage();
  const order = orders.find((o) => o.order_id === orderId);

  // Payment modal state
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  // Receipt preview modal state
  const [isReceiptPreviewOpen, setIsReceiptPreviewOpen] = useState(false);

  // Receipt ref for printing (kept for backward compatibility)
  const receiptRef = useRef();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/signin");
    } else if (user?.user_id) {
      dispatch(getOrdersByUserId(user.user_id));
    }
  }, [dispatch, isAuthenticated, navigate, user]);

  useEffect(() => {
    console.log("Order:", order);
    // document.title = `Order ${order.order_id}`;
  }, [order]);

  const handlePayNowClick = () => {
    if (order && order.payment_status !== "Paid") {
      setIsPaymentModalOpen(true);
    }
  };

  const handlePaymentModalClose = () => {
    setIsPaymentModalOpen(false);
  };

  const handlePaymentSuccess = (paymentData) => {
    // Optionally refresh orders or show success message
    console.log("Payment initiated successfully:", paymentData);
    // You can dispatch an action to refresh orders here if needed
    // dispatch(getOrdersByUserId(user.user_id));
  };

  // Handle receipt preview
  const handleReceiptPreview = () => {
    setIsReceiptPreviewOpen(true);
  };

  const handleReceiptPreviewClose = () => {
    setIsReceiptPreviewOpen(false);
  };

  if (loading) {
    return (
      <div className="myaccount-container">
        <AccountLayout />
        <OrderDetailPageSkeleton />
      </div>
    );
  }

  if (error) {
    return <p className="order-details-error-message">{error}</p>;
  }

  if (!order) {
    return <p className="order-details-no-order-message">Order not found.</p>;
  }

  return (
    <div className="myaccount-container">
      <AccountLayout />
      <div className="order-details-container">
        <div className="order-details-header">
          <button
            onClick={() => navigate("/orders")}
            className="order-details-back-button"
          >
            <FaChevronLeft /> Back to Orders
          </button>
          <div className="order-details-header-content">
            <div className="order-details-header-info">
              <div className="order-details-title-row">
                <h2 className="order-details-title">Order {order.order_id}</h2>
                <button
                  onClick={handleReceiptPreview}
                  className="preview-receipt-button"
                  title="Preview Receipt"
                >
                  <FaEye /> Preview Receipt
                </button>
              </div>

              <p className="order-details-date">
                Placed on{" "}
                {new Date(order.created_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
              <div className="order-status-badges">
                <div className="status-badge">
                  <span className="status-label">Order Status </span>
                  <span
                    className={`order-details-status-text ${order.status.toLowerCase()}`}
                  >
                    {order.status}
                  </span>
                </div>
                <div className="status-badge">
                  <span className="status-label">Payment Status </span>
                  <span
                    className={`order-details-status-text ${order.payment_status.toLowerCase()}`}
                  >
                    {order.payment_status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {order.payment_status.toLowerCase() === "unpaid" && (
          <div className="payment-instructions-card">
            <div className="payment-instructions-header">
              <h3>Payment Instructions</h3>
              {order.payment_status.toLowerCase() === "unpaid" && (
                <span className="payment-pending-badge">Payment Required</span>
              )}
            </div>
            <div className="payment-instructions-content">
              <div className="momo-code-section">
                <span className="momo-label">MTN Mobile Money Code:</span>
                <span className="momo-code">*182*8*1*052158#</span>
                <span className="merchant-name">SONG & STARK COOPERATION</span>
              </div>
              <div className="payment-info-footer">
                <p className="payment-note">
                  Please complete the payment to process your order
                </p>
                <div className="payment-buttons">
                  <button onClick={handlePayNowClick} className="pay-now-btn">
                    Pay Now
                  </button>
                  <button
                    onClick={() => navigate("/contactus")}
                    className="contact-support-btn"
                  >
                    Need Help? Contact Support
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Coupon Information Section */}
        {order.applied_coupon_code && (
          <div className="order-coupon-info-card">
            <h3>Coupon Applied</h3>
            <div className="coupon-details">
              <div className="coupon-code-display">
                <span className="coupon-label">Coupon Code:</span>
                <span className="coupon-code-badge">
                  {order.applied_coupon_code}
                </span>
              </div>
              {order.coupon_discount_amount > 0 && (
                <div className="coupon-discount-display">
                  <span className="discount-label">Discount Applied:</span>
                  <span className="discount-amount">
                    -{formatMoney(order.coupon_discount_amount)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="order-content-grid">
          <div className="order-details-summary">
            {/* Import OrderSummary here */}
            <OrderSummary
              orders={[order]}
              appliedCoupons={
                order.applied_coupon_code
                  ? [
                      {
                        coupon_code: order.applied_coupon_code,
                        coupon_type: order.coupon_type || "fixed",
                        discount: order.coupon_discount_amount, // This is the final calculated amount
                      },
                    ]
                  : []
              }
            />
          </div>

          <div className="order-details-items-ordered">
            <h3>Items Ordered</h3>
            {Array.isArray(order.order_items) &&
            order.order_items.length > 0 ? (
              order.order_items.map((item) => (
                <div
                  key={item.order_item_id}
                  className="order-details-ordered-item"
                >
                  <div className="order-details-item-details flex gap-2">
                    <div className="order-details-left">
                      <img
                        src={getImageUrl(item.product.image)}
                        alt={item.product.name}
                        className="order-details-item-image"
                      />
                    </div>
                    <div className="order-details-right">
                      <p className="order-details-item-name">
                        {item.product.name}
                      </p>
                      <div className="flex gap-2">
                        <p className="order-details-item-quantity">
                          <strong>Quantity:</strong> {item.quantity}
                        </p>
                        <p className="order-details-item-price">
                          <strong>Price:</strong>{" "}
                          {formatMoney(item.price_at_purchase)}
                        </p>
                        {item.option && (
                          <p className="order-details-item-price">
                            <strong>Option:</strong> {item.option.name}
                          </p>
                        )}
                        {item.selected_color && (
                          <p className="order-details-item-price">
                            <strong>Color:</strong> {item.selected_color}
                          </p>
                        )}
                      </div>
                      <div className="write-a-review">
                        <button
                          className="write-a-review-button"
                          onClick={() =>
                            navigate(
                              `/product/${item.product.slug || item.product.product_id}#reviews`
                            )
                          }
                        >
                          Write a Review
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p>No items found for this order.</p>
            )}
          </div>
        </div>

        <ShippingProgress orderProgress={order.order_progress} />

        <div className="order-details-courier-info">
          <h3>Delivery Information</h3>
          <p>Address: {order.address || "Not Provided"}</p>
          {order.tracking_url && (
            <p>
              <a
                href={order.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Track your shipment
              </a>
            </p>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={handlePaymentModalClose}
        order={order}
        onPaymentSuccess={handlePaymentSuccess}
      />

      {/* Receipt Preview Modal */}
      <ReceiptPreviewModal
        isOpen={isReceiptPreviewOpen}
        onClose={handleReceiptPreviewClose}
        order={order}
      />

      {/* Hidden Receipt for Printing (kept for backward compatibility) */}
      <div style={{ display: "none" }} ref={receiptRef}>
        <Receipt order={order} />
      </div>
    </div>
  );
};
export default OrderDetailsPage;
